import { formatTimeStamp } from "util/time"

import { DownloadIcon } from "@chakra-ui/icons"
import { Box, HStack, IconButton } from "@chakra-ui/react"
import { FundingRateServiceContainer } from "container/service/FundingRateServiceContainer"
import {
    BarPrice,
    IChartApi,
    ISeriesApi,
    LineData,
    LineStyle,
    PriceLineOptions,
    TickMarkFormatter,
    createChart,
} from "lightweight-charts"
import { DateTime } from "luxon"
import { useCallback, useEffect, useRef, useState } from "react"
import { CSVLink } from "react-csv"
import { useAsync } from "react-use"
import { FUNDING_RATE_RESOLUTION } from "service/appSync/FundingRateService"

import { formatDateRangeToSecond } from "../../../util/formatFundingRatesQuery"
import { parseFundingRates } from "../../../util/parseFundingRates"
import { DateRangeType } from "../../DateRangePicker"
import { DateRangePopover } from "./DateRangePopover"
import { RESOLUTION_OPTIONS, ResolutionOptions } from "./ResolutionOptions"
import { TooltipOverlay, TooltipProps } from "./TooltipOverlay"

const TOOLTIP_TIME_FORMAT = "yyyy/LL/dd hh:mm a"
const COLOR_CHART_TEXT = "rgba(255, 255, 255, 0.90)"
const COLOR_CHART_LINE = "rgba(255, 255, 255, 0.92)"
const BASE_LINE_OPTIONS: PriceLineOptions = {
    price: 0,
    title: "",
    lineWidth: 1,
    lineVisible: true,
    axisLabelVisible: false,
    lineStyle: LineStyle.Dashed,
    color: COLOR_CHART_LINE,
}
const LINE_OPTIONS: Parameters<IChartApi["addLineSeries"]>[0] = {
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    color: COLOR_CHART_LINE,
    priceLineVisible: false,
    lastValueVisible: false,
    priceFormat: {
        type: "percent",
        minMove: 0.001, // NOTE: original value, not percentage
        precision: 10000,
    },
}
const CHART_OPTIONS: Parameters<typeof createChart>[1] = {
    layout: {
        backgroundColor: "transparent",
        textColor: COLOR_CHART_TEXT,
        fontSize: 10,
    },
    grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
    },
    leftPriceScale: {
        visible: true,
        borderVisible: false,
        autoScale: true,
    },
    rightPriceScale: {
        visible: false,
    },
    overlayPriceScales: {
        borderVisible: true,
    },
    timeScale: {
        borderVisible: false,
        tickMarkFormatter: (time => DateTime.fromSeconds(time as number).toFormat("dd")) as TickMarkFormatter,
    },
}

const getCurrentTooltipData = (data: LineData[]) => {
    if (!data.length) {
        return undefined
    }
    return {
        fundingRate: data[data.length - 1]?.value,
        timeStr: formatTimeStamp(data[data.length - 1]?.time as number, TOOLTIP_TIME_FORMAT),
    }
}

type FundingRateHistoryChartProps = {
    tickerSymbol: string
    isActive?: boolean
}
const CSV_HEADERS = [
    { label: "Funding Rate", key: "value" },
    { label: "Timestamp(UTC)", key: "time" },
    { label: "Local time", key: "timeStr" },
]
const parseCSVDatetimeFormat = (data: LineData[]) => {
    return data.map(obj => {
        return {
            ...obj,
            timeStr: formatTimeStamp(obj.time as number, TOOLTIP_TIME_FORMAT),
        }
    })
}

export const FundingRateChart = ({ tickerSymbol, isActive }: FundingRateHistoryChartProps) => {
    const chartDomRef = useRef<HTMLDivElement>(null)
    const chartRef = useRef<IChartApi>()
    const seriesRef = useRef<ISeriesApi<"Line">>()

    const [tooltipData, setTooltipData] = useState<TooltipProps["data"]>()
    const [mouseOverProps, setMouseOverProps] = useState<TooltipProps["mouseOverProps"]>()

    const [resolution, setResolution] = useState<FUNDING_RATE_RESOLUTION>(RESOLUTION_OPTIONS[0].value)
    const handleResolutionChange = useCallback<(nextValue: FUNDING_RATE_RESOLUTION) => void>(value => {
        setResolution(value)
    }, [])

    // NOTE: Init chart
    useEffect(() => {
        if (!chartDomRef.current || !isActive || !!chartRef.current) {
            return
        }

        chartRef.current = createChart(chartDomRef.current, CHART_OPTIONS)
        seriesRef.current = chartRef.current.addLineSeries(LINE_OPTIONS)
        seriesRef.current.createPriceLine(BASE_LINE_OPTIONS)

        // NOTE: Handle mouse movement
        chartRef.current.subscribeCrosshairMove(({ point, time, seriesPrices }) => {
            if (!chartDomRef.current || !chartRef.current || !seriesRef.current) {
                return
            }

            // NOTE: out of chart
            if (!point || !time) {
                setTooltipData(undefined)
                setMouseOverProps(undefined)
                return
            }

            // NOTE: inside chart
            const { x: mouseX, y: mouseY } = point

            const timeStr = DateTime.fromSeconds(time as number).toFormat(TOOLTIP_TIME_FORMAT)
            const dataPointValue = seriesPrices.get(seriesRef.current) as BarPrice | undefined
            if (!dataPointValue) {
                return
            }
            setTooltipData({ timeStr, fundingRate: dataPointValue })
            setMouseOverProps({
                mouseX,
                mouseY,
                chartWidth: chartDomRef.current.clientWidth,
                chartHeight: chartDomRef.current.clientHeight,
                priceScaleWidth: chartRef.current.priceScale("left").width(),
                timescaleHeight: chartRef.current.timeScale().height(),
            })
        })

        return () => {
            chartRef.current?.remove()
            chartRef.current = undefined
        }
    }, [isActive])

    // NOTE: Handle chart resize
    const resizeChart = useCallback(() => {
        if (!chartRef.current || !chartDomRef.current) {
            return
        }
        chartRef.current.resize(0, 0, true) // NOTE: minimize chart to shrink chartRef.current.clientWidth/Height back to default first
        chartRef.current.resize(chartDomRef.current.clientWidth, chartDomRef.current.clientHeight, true)
        chartRef.current.timeScale().fitContent()
    }, [])
    useEffect(() => {
        window.addEventListener("resize", resizeChart)
        return () => {
            window.removeEventListener("resize", resizeChart)
        }
    }, [resizeChart])

    // NOTO: Fetch data
    const [dateRange, setDateRange] = useState<DateRangeType>([
        DateTime.now().minus({ days: 13 }).toJSDate(),
        new Date(),
    ])

    const { fundingRateService } = FundingRateServiceContainer.useContainer()
    const { value: data } = useAsync(async () => {
        const isOneOfDateRangeEmpty = dateRange.filter(Boolean).length !== 2

        if (!isActive || !fundingRateService || isOneOfDateRangeEmpty) {
            return
        }

        const items = await fundingRateService.listFundingRates(
            tickerSymbol,
            resolution,
            formatDateRangeToSecond(dateRange),
        )

        const parsedData = parseFundingRates(items)

        setTooltipData(getCurrentTooltipData(parsedData))
        return parsedData
    }, [isActive, fundingRateService, tickerSymbol, resolution, dateRange])

    // NOTE: Update chart data
    useEffect(() => {
        if (!chartDomRef.current || !chartRef.current || !seriesRef.current || !data) {
            return
        }
        seriesRef.current.setData(data)
        chartRef.current.priceScale("left").applyOptions({ autoScale: true })
        chartRef.current.timeScale().fitContent()
    }, [data])

    return (
        <>
            <Box
                position="absolute"
                boxSizing="border-box"
                top={14}
                left={2}
                right={2}
                bottom={{ base: 12, lg: 4 }}
                ref={chartDomRef}
            >
                <HStack
                    top={{ base: "auto", lg: 1 }}
                    bottom={{ base: -9, lg: "auto" }}
                    px={3}
                    w="full"
                    zIndex={10}
                    justify={{ base: "center", lg: "flex-end" }}
                    position="absolute"
                >
                    <ResolutionOptions defaultValue={resolution} onChange={handleResolutionChange} />
                    <DateRangePopover dateRange={dateRange} onChange={setDateRange} />
                    <CSVLink
                        data={parseCSVDatetimeFormat(data || [])}
                        headers={CSV_HEADERS}
                        filename={`funding_rate_${resolution}.csv`}
                    >
                        <IconButton size="xs" aria-label="Download" icon={<DownloadIcon />} />
                    </CSVLink>
                </HStack>
                {!!data && !!chartRef.current && (
                    <TooltipOverlay data={tooltipData || getCurrentTooltipData(data)} mouseOverProps={mouseOverProps} />
                )}
            </Box>
        </>
    )
}
